/**
 * Shared transformation for bar/line/area charts.
 *
 * All three consume the same mapping shape and produce the same series structure.
 * Closes audit #19 — chart data-prep duplication.
 */

import type { ColumnDef, BarMapping, LineMapping, AreaMapping } from '@deckspace/types';
import {
  rowsToColumnar,
  pivotByGroup,
  defsByName,
  getColumn,
  sortIndicesByType,
  permute,
  type Series,
} from './pivot.js';

export interface CartesianResult {
  labels: string[];
  datasets: Series[];
}

export function prepCartesianSeries(
  rows: Record<string, unknown>[],
  columnDefs: ColumnDef[],
  mapping: BarMapping | LineMapping | AreaMapping
): CartesianResult {
  const tableData = rowsToColumnar(rows);
  const defs = defsByName(columnDefs);

  // pivotByGroup sorts labels internally; the simple-y and multi-y branches
  // build labels 1:1 with rows and must sort + permute their datasets to match.
  if (mapping.groupBy && !Array.isArray(mapping.y)) {
    const result = pivotByGroup(tableData, mapping.x, mapping.y, mapping.groupBy, defs);
    return { labels: result.labels, datasets: result.datasets };
  }

  const rawLabels = getColumn<unknown>(tableData, mapping.x).map((v) => String(v));
  const order = sortIndicesByType(rawLabels, defs[mapping.x]?.type);
  const labels = permute(rawLabels, order);

  const yCols = Array.isArray(mapping.y) ? mapping.y : [mapping.y];
  const datasets = yCols.map((col) => {
    const rawValues = getColumn<unknown>(tableData, col).map((v) => Number(v) || 0);
    return {
      label: col,
      values: permute(rawValues, order),
      type: defs[col]?.type ?? 'number',
      unit: defs[col]?.unit,
      currency: defs[col]?.currency,
    };
  });

  return { labels, datasets };
}
